import { useState, useRef } from "react";
import { callApiNoHeader } from "../authorization/FetchToken";
import Snackbar from "@mui/material/Snackbar";
import { Alert, Typography } from "@mui/material";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [screenshots, setScreenshots] = useState([]);

  const editorRef = useRef(null);

  // -----------------------
  // INPUT HANDLER
  // -----------------------
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // -----------------------
  // GET MESSAGE (PRESERVES NEW LINES)
  // -----------------------
  const getMessageText = () => {
    if (!editorRef.current) return "";

    let html = editorRef.current.innerHTML;

    return html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<div>/gi, "")
      .replace(/&nbsp;/g, " ")
      .replace(/<img[^>]*>/gi, "\n[📷 screenshot]\n")
      .trim();
  };

  // -----------------------
  // PASTE IMAGE HANDLER
  // -----------------------
  const handlePaste = (e) => {
    const items = e.clipboardData.items;

    for (let item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();

        const file = item.getAsFile();
        const reader = new FileReader();

        reader.onload = () => {
          const img = reader.result;

          setScreenshots((prev) => [...prev, img]);

          const br1 = document.createElement("br");

          const imgTag = document.createElement("img");
          imgTag.src = img;
          imgTag.style.maxWidth = "200px";
          imgTag.style.display = "block";
          imgTag.style.margin = "8px 0";

          const br2 = document.createElement("br");

          editorRef.current.appendChild(br1);
          editorRef.current.appendChild(imgTag);
          editorRef.current.appendChild(br2);

          editorRef.current.scrollTop = editorRef.current.scrollHeight;
        };

        reader.readAsDataURL(file);
      }
    }
  };

  // -----------------------
  // SUBMIT
  // -----------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const message = getMessageText();

    if (!form.email || !form.name || !message) {
      setError("Please fill all fields");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await callApiNoHeader("/contactForm", {
        name: form.name,
        email: form.email,
        message,
        screenShots: screenshots,
      });

      if (res) {
        setSuccess("Message sent successfully ✔");

        setForm({ name: "", email: "" });
        setScreenshots([]);

        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
      }
    } catch (err) {
      setError("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
  style={{
    minHeight: "calc(100vh - 200px)",
    paddingBottom: "100px",
  }}
>
    <div
    style={{
      maxWidth: 620,
      maxHeight: 1000,
      margin: "100px auto 150px auto", // top right bottom left
      padding: "24px",
      borderRadius: "12px",
      background: "#fff",
      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      fontFamily: "Inter, Arial",
    }}
  >
      {/* SNACKBAR SUCCESS */}
      <Snackbar
        open={!!success}
        autoHideDuration={30000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSuccess("")} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* SNACKBAR ERROR */}
      <Snackbar
        open={!!error}
        autoHideDuration={30000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setError("")} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <div style={{ marginTop: 16, fontSize: 14 }}>
      <p>Kindly add as much detail as possible to the message.</p>

      <p>
        You can paste screenshots directly into the message. This will help us serve you better.
      </p>

      <p>
        You may also directly email your requests using the email address provided in the Contact section.
      </p>
    </div>

      <h2 style={{ fontSize: 22, fontWeight: 700 }}>Contact Us</h2>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* NAME */}
        <input
          name="name"
          placeholder="Your name"
          value={form.name}
          onChange={handleChange}
          style={inputStyle}
        />

        {/* EMAIL */}
        <input
          name="email"
          placeholder="Your email"
          value={form.email}
          onChange={handleChange}
          style={inputStyle}
        />

        {/* MESSAGE BOX */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onPaste={handlePaste}
          style={{
            height: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            borderRadius: 8,
            padding: 10,
            outline: "none",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        />

        {/* IMAGE PREVIEW */}
        {screenshots.length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {screenshots.map((img, i) => (
              <img
                key={i}
                src={img}
                alt="shot"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: 6,
                  border: "1px solid #ddd",
                }}
              />
            ))}
          </div>
        )}

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            border: "none",
            background: loading ? "#94a3b8" : "#2563eb",
            color: "white",
            fontWeight: 600,
          }}
        >
          {loading ? "Sending..." : "Send Message"}
        </button>
      </form>
    </div>
    </div>
  );
}

// -----------------------
const inputStyle = {
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  outline: "none",
  fontSize: "14px",
};